import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    manufacturerType: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const menuItems = [
    { label: 'ホーム', href: '/', type: 'page' },
    { label: 'ホイール最新ニュース', href: '#news', type: 'anchor' },
    { label: '使い方ガイド', href: '/guide', type: 'page' },
    { label: 'お問い合わせ', href: '/contact', type: 'page' },
  ];

  const handleMenuClick = (href: string, type: string) => {
    setIsMenuOpen(false);
    
    if (type === 'anchor') {
      // アンカーリンクの場合 - TOPページのセクションへ
      window.location.href = '/' + href;
    } else if (href === '/') {
      // ホームの場合 - TOPページへ移動
      window.location.href = '/';
    } else {
      // 他のページへの遷移
      window.location.href = href;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d59nfehdn6dhfpiab99g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          name: '',
          email: '',
          phone: '',
          manufacturerType: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-5 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #00d4ff 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <p className="text-[#39ff14] text-xs tracking-[0.2em] uppercase mb-4">
            MANUFACTURER CONTACT
          </p>
          <h1 className="text-white text-5xl md:text-6xl font-black mb-6">
            お問い合わせ
          </h1>
          <p className="text-[#888888] text-base leading-relaxed max-w-2xl mx-auto">
            企業様からのお問い合わせを承っております。<br />
            製品掲載、提携、その他ご相談など、お気軽にお問い合わせください。
          </p>
        </motion.div>
      </section>

      {/* Contact Form Section */}
      <section ref={ref} className="py-20 px-5 bg-black">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <motion.form
            id="manufacturer-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-[#1a1a1a] rounded-3xl p-10 border border-[#333333]"
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-white text-sm font-medium mb-2">
                    会社名 <span className="text-[#ff4444]">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors"
                    placeholder="株式会社〇〇"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                    ご担当者名 <span className="text-[#ff4444]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors"
                    placeholder="山田 太郎"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                    メールアドレス <span className="text-[#ff4444]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors"
                    placeholder="example@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-white text-sm font-medium mb-2">
                    電話番号 <span className="text-[#ff4444]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="manufacturerType" className="block text-white text-sm font-medium mb-2">
                  メーカー種別 <span className="text-[#ff4444]">*</span>
                </label>
                <select
                  id="manufacturerType"
                  name="manufacturerType"
                  value={formData.manufacturerType}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors cursor-pointer appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23888888'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em',
                    paddingRight: '2.5rem'
                  }}
                >
                  <option value="">選択してください</option>
                  <option value="ホイールメーカー">ホイールメーカー</option>
                  <option value="タイヤメーカー">タイヤメーカー</option>
                  <option value="カスタムパーツメーカー">カスタムパーツメーカー</option>
                  <option value="自動車メーカー">自動車メーカー</option>
                  <option value="販売代理店">販売代理店</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="subject" className="block text-white text-sm font-medium mb-2">
                  お問い合わせ種別 <span className="text-[#ff4444]">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors cursor-pointer appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23888888'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em',
                    paddingRight: '2.5rem'
                  }}
                >
                  <option value="">選択してください</option>
                  <option value="製品掲載のご相談">製品掲載のご相談</option>
                  <option value="提携・協業のご提案">提携・協業のご提案</option>
                  <option value="3Dシミュレーター掲載について">3Dシミュレーター掲載について</option>
                  <option value="広告・プロモーションについて">広告・プロモーションについて</option>
                  <option value="技術的なご質問">技術的なご質問</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                  お問い合わせ内容 <span className="text-[#ff4444]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={8}
                  className="w-full bg-[#0a0a0a] text-white text-sm px-4 py-3 rounded-xl border border-[#333333] focus:border-[#00d4ff] focus:outline-none transition-colors resize-none"
                  placeholder="お問い合わせ内容を詳しくご記入ください（500文字以内）"
                />
                <p className="text-[#666666] text-xs mt-2 text-right">
                  {formData.message.length} / 500
                </p>
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#39ff14]/10 border border-[#39ff14] rounded-xl p-4"
                >
                  <p className="text-[#39ff14] text-sm text-center">
                    お問い合わせを受け付けました。ご連絡ありがとうございます。<br />
                    担当者より2営業日以内にご返信させていただきます。
                  </p>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#ff4444]/10 border border-[#ff4444] rounded-xl p-4"
                >
                  <p className="text-[#ff4444] text-sm text-center">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#00d4ff] to-[#39ff14] text-black text-sm font-bold py-4 rounded-xl hover:shadow-lg hover:shadow-[#00d4ff]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </span>
                ) : (
                  '送信する'
                )}
              </button>
            </div>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
            className="mt-10 bg-[#1a1a1a] rounded-2xl p-8 border border-[#333333]"
          >
            <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-information-line text-[#00d4ff]"></i>
              お問い合わせについて
            </h3>
            <div className="space-y-3 text-[#888888] text-sm leading-relaxed">
              <p>• お問い合わせいただいた内容は、通常2営業日以内にご返信いたします。</p>
              <p>• 製品掲載をご希望の場合は、製品の詳細情報や画像データをご用意ください。</p>
              <p>• 提携・協業のご提案については、別途詳細な資料をお送りいただく場合がございます。</p>
              <p>• お急ぎの場合は、お電話でのお問い合わせもご利用ください。</p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
